import React, {
	type ReactNode,
	createContext,
	useContext,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import type { HoverPopoverRefContent } from '../types.tsx';
import { useLazyRef } from '../utils.tsx';

type DeleteListener = (relayFieldId: string) => void;
interface HoverPopoverContentContextValue {
	setCellIdByRef: (cell: HTMLTableCellElement, relayFieldId: string) => void;
	deleteCellIdByRef: (cell: HTMLTableCellElement) => void;
	getCellIdByRef: (cell: HTMLTableCellElement) => string | undefined;
	setHoverPopoverContentById: (relayFieldId: string, content: HoverPopoverRefContent) => void;
	deleteHoverPopoverContentById: (relayFieldId: string) => void;
	getHoverPopoverContentById: (relayFieldId: string) => HoverPopoverRefContent | undefined;
	subscribeHoverPopoverContentDeleteById: (
		relayFieldId: string,
		listener: DeleteListener,
	) => () => void;
}
interface HoverPopoverContentProviderProps {
	children: ReactNode;
}

const HoverPopoverContentContext = createContext<HoverPopoverContentContextValue | undefined>(
	undefined,
);

export const HoverPopoverContentProvider: React.FC<HoverPopoverContentProviderProps> = ({
	children,
}) => {
	/**
	 * cellRefRelayIdMap is a map of cell refs to relay field IDs. This is used by the
	 * event delegator to lookup which field is hovered by table cell ref.
	 */
	const cellRefRelayIdMap = useLazyRef(() => new Map<HTMLTableCellElement, string>());
	/**
	 * relayIdPopoverContentMap is a map relay field IDs to hover popover content.
	 * This is used by the hover popover state provider to lookup what the content for a hover popover
	 * is by a given relay field ID. In conjunction with the cellRefRelayIdMap above, this allows us to
	 * lookup the content for a hover popover by a given table cell ref.
	 */
	const relayIdPopoverContentMap = useLazyRef(() => new Map<string, HoverPopoverRefContent>());
	/**
	 * deleteListeners is a map of relay field IDs to a set of delete listeners. See comments below on deleteHoverPopoverContentById.
	 */
	const deleteListeners = useLazyRef(() => new Map<string, Set<DeleteListener>>());

	const setCellIdByRef = useCallback(
		(cell: HTMLTableCellElement, relayFieldId: string) => {
			cellRefRelayIdMap.current.set(cell, relayFieldId);
		},
		[cellRefRelayIdMap],
	);

	const getCellIdByRef = useCallback(
		(cell: HTMLTableCellElement) => {
			return cellRefRelayIdMap.current.get(cell);
		},
		[cellRefRelayIdMap],
	);

	const deleteCellIdByRef = useCallback(
		(cell: HTMLTableCellElement) => {
			cellRefRelayIdMap.current.delete(cell);
		},
		[cellRefRelayIdMap],
	);

	const setHoverPopoverContentById = useCallback(
		(relayFieldId: string, content: HoverPopoverRefContent) => {
			relayIdPopoverContentMap.current.set(relayFieldId, content);
		},
		[relayIdPopoverContentMap],
	);

	const getHoverPopoverContentById = useCallback(
		(relayFieldId: string) => {
			return relayIdPopoverContentMap.current.get(relayFieldId);
		},
		[relayIdPopoverContentMap],
	);

	/**
	 * When a user hovers on a cell a popover opens. Then if they open inline edit we need the popover to close.
	 *
	 * The popover is attached using the hoverPopoverContentRef. When the popup is mounted, subscribeHoverPopoverContentDeleteById
	 * is called which allows us to subscribe to calls to deleteHoverPopoverContentById. When the component unmounts, the hover popover content ref will call this
	 * delete method (i.e user switches from read to edit mode), which calls any listeners (e.g closePopup).
	 */
	const deleteHoverPopoverContentById = useCallback(
		(relayFieldId: string) => {
			const isDeleted = relayIdPopoverContentMap.current.delete(relayFieldId);
			if (isDeleted && deleteListeners.current.has(relayFieldId)) {
				for (const listener of deleteListeners.current.get(relayFieldId) ?? []) {
					listener(relayFieldId);
				}
				deleteListeners.current.delete(relayFieldId);
			}
		},
		[relayIdPopoverContentMap, deleteListeners],
	);

	const subscribeHoverPopoverContentDeleteById = useCallback(
		(relayFieldId: string, listener: DeleteListener) => {
			if (!deleteListeners.current.has(relayFieldId)) {
				deleteListeners.current.set(relayFieldId, new Set());
			}

			deleteListeners.current.get(relayFieldId)?.add(listener);

			return () => {
				const listeners = deleteListeners.current.get(relayFieldId);
				if (listeners) {
					listeners.delete(listener);
					if (listeners.size === 0) {
						deleteListeners.current.delete(relayFieldId);
					}
				}
			};
		},
		[deleteListeners],
	);

	const contextValue: HoverPopoverContentContextValue = useMemo(
		() => ({
			setCellIdByRef,
			deleteCellIdByRef,
			getCellIdByRef,
			setHoverPopoverContentById,
			deleteHoverPopoverContentById,
			getHoverPopoverContentById,
			subscribeHoverPopoverContentDeleteById,
		}),
		[
			setCellIdByRef,
			deleteCellIdByRef,
			getCellIdByRef,
			setHoverPopoverContentById,
			deleteHoverPopoverContentById,
			getHoverPopoverContentById,
			subscribeHoverPopoverContentDeleteById,
		],
	);

	return (
		<HoverPopoverContentContext.Provider value={contextValue}>
			{children}
		</HoverPopoverContentContext.Provider>
	);
};

export const useHoverPopoverContent = (): HoverPopoverContentContextValue => {
	const context = useContext(HoverPopoverContentContext);
	if (context === undefined) {
		throw new Error('useHoverPopoverContent must be used within a HoverPopoverContentProvider');
	}
	return context;
};

export type HoverPopoverContentRef = ((node: HTMLElement | null) => void) | null;

// Returns a ref to bind to hover popover content.
// It adds the field's relayFieldID and its corresponding content as well as the node its appended to to the relayIdPopoverContentMap.
// lastNode ensures that when the content ref unmounts, the relayFieldID and its corresponding content is removed from the relayIdPopoverContentMap.
export const useHoverPopoverContentRef = (relayFieldId?: string) => {
	const lastNode = useRef<HTMLElement | null>(null);
	const { setHoverPopoverContentById, deleteHoverPopoverContentById } = useHoverPopoverContent();

	const hoverPopoverContentRefFactory = useCallback(
		(content: Omit<HoverPopoverRefContent, 'contentElement'>) => (node: HTMLElement | null) => {
			if (node && relayFieldId) {
				setHoverPopoverContentById(relayFieldId, { ...content, contentElement: node });
				lastNode.current = node;
			} else if (lastNode.current && relayFieldId) {
				deleteHoverPopoverContentById(relayFieldId);
				lastNode.current = null;
			}
		},
		[relayFieldId, setHoverPopoverContentById, deleteHoverPopoverContentById],
	);
	return hoverPopoverContentRefFactory;
};
